import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { plupload } from '../../../../../a-react-app/src/_common/libs/pluploadLite/plupload';
import { spliceOut } from 'components/sharpr/util/spliceOut';
import { ensure } from 'components/sharpr/ensure/ensure';
import moment from 'moment/moment';
import _ from 'lodash';
import RunQueue from 'run-queue';

export function PostImportRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.PostImport', {
		url: '/admin/import/posts',
		controllerAs: 'vm',
		controller: PostImportCtrl,
		templateUrl: '/ng/hub/Post/Import/PostImportCtrl.html?' + window.BUILD,
		permission: ['all'],
		resolve: {
			style: () => AssetManager.css('/ng/hub/Post/Import/PostImportCtrl.css'),
			$title: () => 'Import Posts',
			publishers: ApiResourcify.endpoint('/users/search?role=!observer&limit=100'),
			categories: ApiResourcify.endpoint('/categories'),
			classifications: ApiResourcify.endpoint('/classifications'),
			icons: ApiResourcify.endpoint('/classifications/icons'),
		},
	});
}

export class PostImportCtrl {
	constructor(
		$q,
		$scope,
		$interval,
		$state,
		Identity,
		Cfg,
		Api,
		Post,
		Att,
		modal,
		categories,
		classifications,
		icons,
		publishers
	) {
		'ngInject';
		// save injected services
		this.$q = $q;
		this.$state = $state;
		this.Cfg = Cfg;
		this.Identity = Identity;
		this.Api = Api;
		this.Post = Post;
		this.Att = Att;
		this.modal = modal;
		// save injected data
		this.categories = _.sortBy(categories, 'name');
		this.classifications = _.sortBy(classifications, 'name');
		this.publishers = this.processPublishers(publishers);
		this.icons = icons;
		// setup
		this.maxSaveConcurrency = 3;
		this.step = 'drag';
		this.dateFormat = 'M/D/YYYY h:mma';
		this.files = [];
		this.filesWithErrors = [];
		this.importProgress = 0;
		this.sortBy = 'name';
		const progressInterval = $interval(this.calculateProgress.bind(this), 1000);
		$scope.$on('$destroy', () => $interval.cancel(progressInterval));
		// constants for use in templates
		this.QUEUED = plupload.QUEUED;
		this.UPLOADING = plupload.UPLOADING;
		this.FAILED = plupload.FAILED;
		this.DONE = plupload.DONE;
	}

	processPublishers(publishers) {
		const choices = [];
		const me = this.Identity.user();
		let withoutMe = _.without(publishers, { id: me.id });
		withoutMe = _.sortBy(withoutMe, ['fname_preferred', 'fname']);
		[me, ...withoutMe].forEach(user => {
			choices.push({
				id: user.id,
				name: ((user.fname_preferred || user.fname) + ' ' + (user.lname_preferred || user.lname)).trim(),
			});
		});
		return choices;
	}

	calculateProgress() {
		// steps:
		// queued
		// uploading
		// uploaded
		// attachment created
		// post created
		let done = 0;
		let total = this.files.length * 4 || 1;
		this.files.forEach(file => {
			done += file.status == this.UPLOADING || file.status == this.DONE ? 1 : 0;
			done += file.status == this.DONE ? 1 : 0;
			done += file.attId ? 1 : 0;
			done += file.post ? 1 : 0;
		});
		this.importProgress = 100 * (done / total);
	}

	goStep(name) {
		if (name == 'taxonomy') {
			this.clearFileErrors();
			this.gatherTaxonomy();
		}
		if (name == 'preview') {
			this.applyTaxonomy();
			// create lookups
			this.categoriesById = _.indexBy(this.categories, 'id');
			this.classificationsById = _.indexBy(this.classifications, 'id');
		}
		if (name == 'save') {
			this.save();
		}
		this.step = name;
	}

	addFile(file) {
		file.breadcrumbs = (file.dirs || []).join(' / ');
		this.files.push(file);
	}

	cancelUpload(file) {
		file.isDeleted = true;
		spliceOut(this.files, file);
		if (file.attId) {
			this.Api.delete(`/attachments/${file.attId}`);
		}
		if (file.post) {
			this.Api.delete(`/posts/${file.post.id}`);
		}
		if (file.status == this.QUEUED || file.status == this.UPLOADING) {
			file.cancel();
		}
	}

	addPostFromFile(file) {
		// file is in plupload2 bucket and attachment record created
		// setup post info
		const postDate = moment(file.lastModifiedDate).format();
		const post = {
			intel_text: '',
			rich_text_body: this.Cfg.read('Post.processing_text'),
			headline: this.filenameToHeadline(file.name),
			url: '',
			published_at: postDate,
			submitted_by_id: this.publishers[0].id,
			published_by_id: this.publishers[0].id,
			source_link: '',
			source_name: 'File Import',
			source_date: postDate,
			submission_medium: 'intel',
			import_breadcrumbs: file.dirs,
			import_attachment_id: file.attId,
			publish: true,
		};
		if (!this.queue || this.queue.finished) {
			this.queue = new RunQueue({ maxConcurrency: 1 });
		}
		this.queue.add(1, () => {
			return new Promise((resolve, reject) => {
				if (file.isDeleted) {
					return Promise.resolve();
				}
				this.Api.create('/posts', post)
					.then(newId => {
						if (file.isDeleted) {
							return this.Api.delete(`/posts/${newId}`);
						}
						post.id = newId;
						return this.Post.attachFile(post, {
							id: file.attId,
							name: file.name,
						}).then(() => {
							file.post = post;
						}, resolve);
					}, resolve)
					.then(() => {
						setTimeout(resolve, 500);
					}, resolve);
			});
		});
		this.queue.run();
	}

	// compare with FileUtil.php ::escapeFilename
	filenameToHeadline(name) {
		// remove extension
		name = name.replace(/\.[\w\d]+$/, '');
		// remove special characters
		name = name.replace(/[^\w._, -]+/g, '');
		// change underscores to spaces
		name = name.replace(/_+/g, ' ');
		return name;
	}

	gatherTaxonomy() {
		// mode is: taxonomy
		// setup list of entries
		const categoriesByName = _.indexBy(this.categories, cat => cat.name.toLocaleLowerCase());
		const classificationsByName = _.indexBy(this.classifications, cls => cls.name.toLocaleLowerCase());
		const taxEntries = this.taxEntries ? _.indexBy(this.taxEntries, 'name') : {};
		this.files.forEach(file => {
			file.dirs.forEach(dir => {
				if (taxEntries[dir]) {
					return;
				}
				const lowerDir = dir.toLocaleLowerCase();
				const category = categoriesByName[lowerDir];
				const classification = classificationsByName[lowerDir];
				taxEntries[dir] = {
					name: dir,
					type: category ? 'category' : classification ? 'classification' : 'tag',
					category: category,
					category_id: category ? category.id : '',
					classification: classification,
					classification_id: classification ? classification.id : '',
					tag: category || classification ? '' : dir,
				};
			});
		});
		this.taxEntries = _.sortBy(_.values(taxEntries), 'name');
	}

	maybeAddCategory(taxEntry, newValue) {
		if (newValue != 'new') {
			// a regular category was selected
			return;
		}
		// the "Add new..." category was selected; prompt for category name
		this.modal
			.prompt({
				text: 'Category name:',
				heading: 'Add new category',
				inputText: taxEntry.name,
			})
			.then(
				name => {
					// check for duplicate
					const existing = _.find(this.categories, { name });
					if (existing) {
						taxEntry.category_id = existing.id;
						return;
					}
					if (name.indexOf(';') > -1) {
						alert('Category names cannot contain semicolons. Please choose a different name.');
						taxEntry.category_id = '';
						return;
					}
					if (/^\d+$/.test(name)) {
						alert('Category names cannot consist of only numbers. Please choose a different name.');
						taxEntry.category_id = '';
						return;
					}
					if (name.length > 50) {
						alert('Category names cannot be more than 50 characters. Please choose a different name.');
						taxEntry.category_id = '';
						return;
					}
					// create it
					const data = { name };
					this.Api.create('/categories', data).then(newCatId => {
						data.id = newCatId;
						this.categories.push(data);
						// _.sortBy returns a brand new array, so sh-select's "<" binding will trigger $onChanges
						this.categories = _.sortBy(this.categories, 'name');
						taxEntry.category_id = newCatId;
					});
				},
				() => {
					// canceled
					taxEntry.category_id = '';
				}
			);
	}

	maybeAddClassification(taxEntry, newValue) {
		if (newValue != 'new') {
			// a regular classification was selected
			return;
		}
		// the "Add new..." classification was selected; prompt for classification name
		this.modal
			.prompt({
				text: 'Classification name:',
				heading: 'Add new classification',
				inputText: taxEntry.name,
			})
			.then(
				name => {
					// check for duplicate
					const existing = _.find(this.classifications, { name });
					if (existing) {
						taxEntry.classification_id = existing.id;
						return;
					}
					// create it
					const data = {
						name: name,
						color: this.getRandomColor(),
						icon: this.getRandomIcon(),
					};
					this.Api.create('/classifications', data).then(newClsId => {
						data.id = newClsId;
						this.classifications.push(data);
						// _.sortBy returns a brand new array, so sh-select's "<" binding will trigger $onChanges
						this.classifications = _.sortBy(this.classifications, 'name');
						taxEntry.classification_id = newClsId;
					});
				},
				() => {
					// canceled
					taxEntry.classification_id = '';
				}
			);
	}

	applyTaxonomy() {
		// mode is: preview
		// gather preview
		const entriesByName = _.indexBy(this.taxEntries, 'name');
		const previewsByFilename = this.previews ? _.indexBy(this.previews, 'name') : {};
		this.files.forEach(file => {
			let isDirty = false;
			let preview = previewsByFilename[file.name];
			if (preview) {
				preview.tags = preview.tag_names.split(/\s*,\s*/);
			} else {
				preview = {
					file: file,
					timestamp: moment(file.lastModifiedDate).unix(),
					publisher_id: this.publishers[0].id,
					date: moment(file.lastModifiedDate).format(this.dateFormat),
					name: this.filenameToHeadline(file.name),
					category_id: undefined,
					classification_id: undefined,
					tags: [],
				};
			}
			file.dirs.forEach(dir => {
				const entry = entriesByName[dir];
				if (!entry) {
					return;
				}
				if (entry.type == 'category') {
					isDirty = true;
					preview.category_id = entry.category_id;
				} else if (entry.type == 'classification') {
					isDirty = true;
					preview.classification_id = entry.classification_id;
				} else if (entry.type == 'tag') {
					isDirty = true;
					preview.tags = preview.tags.concat(entry.tag.split(/\s*,\s*/));
				} else if (entry.type == 'none') {
					preview.category_id = undefined;
					preview.classification_id = undefined;
				}
			});
			preview.isDirty = isDirty;
			preview.tag_names = _.uniq(preview.tags).join(', ');
			previewsByFilename[file.name] = preview;
		});
		// set previews
		this.previews = [];
		this.files.forEach(file => {
			this.previews.push(previewsByFilename[file.name]);
		});
		this.sort();
	}

	sort() {
		const name = this.sortBy.replace(/^-/, '');
		const multiplier = this.sortBy.slice(0, 1) == '-' ? -1 : 1;
		const comparator = (a, b) => {
			if (typeof a[name] === 'number') {
				return (a[name] - b[name]) * multiplier;
			}
			return a[name].localeCompare(b[name]) * multiplier;
		};
		this.previews.sort(comparator);
	}

	save() {
		// create queue to save classifications and categories first and posts later
		const queue = new RunQueue({ maxConcurrency: this.maxSaveConcurrency });
		// create array of saved items
		this.saved = [];
		// save each post
		this.previews.forEach(preview => {
			const postId = preview.file.post.id;
			if (preview.isDirty) {
				// patch each post
				queue.add(1, () => {
					if (preview.file.isDeleted) {
						return;
					}
					return this.saveOne(preview).then(() => {
						this.pushSaved(postId, preview);
					});
				});
			} else {
				// already saved and no changes
				this.pushSaved(postId, preview);
			}
		});
		// GO!
		queue.run();
	}

	pushSaved(postId, preview) {
		this.saved.push({
			id: postId,
			file_name: preview.name,
			classification_name: ensure.object(this.classificationsById[preview.classification_id]).name,
			category_name: ensure.object(this.categoriesById[preview.category_id]).name,
			tag_names: preview.tag_names,
		});
	}

	saveOne(preview) {
		// handle date
		let date = moment(preview.date);
		if (!date.isValid()) {
			date = moment();
		}
		// save changes
		const postId = preview.file.post.id;
		const data = {
			headline: preview.name,
			published_at: date.format(), // ISO-8601
			submitted_by_id: preview.publisher_id,
			published_by_id: preview.publisher_id,
			category: preview.category_id,
			classification_id: preview.classification_id,
			tags: preview.tag_names.split(/\s*,\s*/),
		};
		return this.Api.patch(`/posts/${postId}`, data).then(
			() => {
				// add a saved record
				preview.isDirty = false;
			},
			() => {}
		);
	}

	onDateChange(evt, preview) {
		const dateStr = preview.date.trim();
		const date = moment(dateStr);
		let formatted;
		if (date.isValid()) {
			if (dateStr.match(/^[\d\/-]+$/)) {
				formatted = date.format('M/D/YYYY') + ' 12:00pm';
			} else {
				formatted = date.format(this.dateFormat);
			}
			preview.date = formatted;
			preview.timestamp = date.unix();
		} else {
			this.modal.alert({ escapedHtml: `Date "${_.escape(dateStr)}" is not in a valid date format.` }).then(() => {
				try {
					evt.target.focus();
				} catch (e) {}
			});
		}
	}

	getRandomColor() {
		return '#' + [0, 0, 0].map(() => _.repeat(_.random(3, 9).toString(16), 2)).join('');
	}

	getRandomIcon() {
		const icon = this.icons[_.random(0, this.icons.length)];
		return icon.name || 'home6';
	}

	details(saved) {
		this.modal.state('hub.PostDetail', {
			heading: 'Post Details',
			params: { id: saved.id },
		});
	}

	viewSavedFile(saved) {
		this.Att.popupPost({
			id: saved.id,
			headline: saved.file_name,
		});
	}

	viewPreviewFile(preview) {
		this.Att.popupPost({
			id: preview.file.post.id,
			headline: preview.name,
		});
	}

	edit(saved) {
		const update = () => {
			this.Api.get(`/v3/posts/${saved.id}`).then(post => {
				saved.file_name = post.headline;
				saved.category_name = ensure.object(post.category).name;
				saved.classification_name = ensure.object(post.classification).name;
				saved.tag_names = post.tags.map(t => t.name).join(', ');
			});
		};
		this.Post.edit(saved.id).then(update, update);
	}

	delete(saved) {
		this.Post.confirmDelete({
			id: saved.id,
			headline: saved.file_name,
		}).then(
			() => {
				const idx = this.saved.indexOf(saved);
				this.saved.splice(idx, 1);
				this.previews.splice(idx, 1);
			},
			() => {}
		);
	}

	deleteAllFiles() {
		const queue = new RunQueue({ maxConcurrency: this.maxSaveConcurrency });
		this.files.forEach(file => {
			file.isDeleted = true;
			queue.add(1, () => {
				if (file.status != this.DONE) {
					file.cancel();
					spliceOut(this.files, file);
					return;
				}
				const tasks = [];
				if (file.attId) {
					tasks.push(this.Api.delete(`/attachments/${file.attId}`));
				}
				if (file.post) {
					tasks.push(this.Api.delete(`/posts/${file.post.id}`));
				}
				return this.$q.all(tasks).then(() => {
					spliceOut(this.files, file);
				});
			});
		});

		this.clearFileErrors();

		queue.run();
	}

	importMore() {
		this.$state.reload();
	}

	/**
	 * When a file upload fails, add it to the list of files with errors to display
	 * a an error message on the UI.
	 * @param {Object} file  Information about the file. Must at least include `s3Name`.
	 * @param {Object} error  Error information from plupload.
	 * @param {String} message Text that can be displayed to the user.
	 */
	handleUploadErrors(file, error, message, header) {
		file.status = this.FAILED;

		if (!this.filesWithErrors.find(f => f.name === file.name)) {
			this.filesWithErrors.push(file);
		}
	}

	removeErrorMessage(fileName) {
		this.filesWithErrors = this.filesWithErrors.filter(f => f.name !== fileName);
	}

	clearFileErrors() {
		this.filesWithErrors = [];
	}
}
